import { useEffect, useMemo, useState } from 'preact/hooks';
import { CollapsibleSection } from '@/components/CollapsibleSection';
import { Direction } from '@/components/Direction';
import { IngredientItem } from '@/components/IngredientItem';
import { Settings } from '@/components/Settings';
import {
  defaultSelection as buildDefaultSelection,
  choiceAddTime,
  resolveOptionRefs,
} from '@/options';
import { recipes } from '@/recipes/_generated';
import { clampServings, multiplierFor } from '@/scaling';
import { tagLabel } from '@/tags';
import { addTimes, formatTime, type Time, time } from '@/time';
import type { Recipe, UnitSystem, When } from '@/types';
import { NotFound } from './NotFound';

function totalTimeFor(recipe: Recipe, selection: Record<string, string>): Time | undefined {
  const hasOptions = (recipe.options?.length ?? 0) > 0;
  if (recipe.totalTime === undefined && !hasOptions) return undefined;
  let t: Time = recipe.totalTime ?? time();
  for (const opt of recipe.options ?? []) {
    const choice = opt.choices.find((c) => c.key === selection[opt.key]);
    if (choice) t = addTimes(t, choiceAddTime(choice));
  }
  return t;
}

function summaryParts(recipe: Recipe, selection: Record<string, string>): string[] {
  const parts: string[] = [];
  const t = totalTimeFor(recipe, selection);
  if (t !== undefined) parts.push(formatTime(t));
  parts.push(`${recipe.ingredients.length} ingredients`);
  return parts;
}

function matchesWhen(when: When | undefined, selection: Record<string, string>): boolean {
  return !when || selection[when[0]] === when[1];
}

function toggleAt<T>(arr: T[], i: number, update: (item: T) => T): T[] {
  return arr.map((item, j) => (i === j ? update(item) : item));
}

function AmountsToggle({ on, onToggle }: { on: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      class={`btn ${on ? '' : 'btn--on'}`}
      onClick={(e) => {
        e.stopPropagation();
        onToggle();
      }}
    >
      {on ? 'Hide Amounts' : 'Show Amounts'}
    </button>
  );
}

interface Props {
  slug: string;
}

export function RecipeView({ slug }: Props) {
  const entry = recipes.find((r) => r.slug === slug);
  if (!entry) return <NotFound />;
  const recipe = entry.recipe;
  const [appliedServings, setAppliedServings] = useState(() =>
    clampServings({ recipe, servings: recipe.servings ?? 1 }),
  );
  const [unitSystem, setUnitSystem] = useState<UnitSystem>('imperial');
  const [selection, setSelection] = useState<Record<string, string>>(() =>
    buildDefaultSelection(recipe.options),
  );
  const multiplier = multiplierFor({ recipe, servings: appliedServings });
  const filteredDirections = useMemo(
    () =>
      recipe.directions
        .filter((d) => matchesWhen(d.when, selection))
        .map((d) => ({
          ...d,
          bullets: d.bullets
            .filter((b) => typeof b === 'string' || matchesWhen(b.when, selection))
            .map((b) => (typeof b === 'string' ? b : b.text))
            .map((text) => resolveOptionRefs(text, recipe.options, selection)),
        })),
    [recipe.directions, recipe.options, selection],
  );
  const [checkedIngredients, setCheckedIngredients] = useState<boolean[]>(() =>
    recipe.ingredients.map(() => false),
  );
  const [checkedDirections, setCheckedDirections] = useState<boolean[][]>(() =>
    filteredDirections.map((d) => d.bullets.map(() => false)),
  );
  useEffect(() => {
    setCheckedDirections(filteredDirections.map((d) => d.bullets.map(() => false)));
  }, [filteredDirections]);
  const [showAmounts, setShowAmounts] = useState(true);

  const ingredientsByName = useMemo(
    () => new Map(recipe.ingredients.map((i) => [i.name, i])),
    [recipe],
  );

  const allIngredientsChecked = checkedIngredients.every(Boolean);
  const allDirectionsChecked = checkedDirections.every((d) => d.every(Boolean));

  const toggleIngredient = (i: number) =>
    setCheckedIngredients((arr) => toggleAt(arr, i, (v) => !v));

  const toggleBullet = (d: number, b: number) =>
    setCheckedDirections((arr) => toggleAt(arr, d, (dir) => toggleAt(dir, b, (v) => !v)));

  const toggleDirection = (d: number) =>
    setCheckedDirections((arr) =>
      toggleAt(arr, d, (dir) => {
        const next = !dir.every(Boolean);
        return dir.map(() => next);
      }),
    );

  return (
    <>
      <div class="recipe__head">
        <h1 class="recipe__title">{recipe.title}</h1>
        <div class="recipe__summary">
          {summaryParts(recipe, selection).join(' · ')}
          {recipe.source ? (
            <>
              {' · '}
              <a href={recipe.source} target="_blank" rel="noreferrer">
                Source
              </a>
            </>
          ) : null}
        </div>
        {recipe.tags ? (
          <div class="recipe__tags">
            {Object.values(recipe.tags)
              .filter(Boolean)
              .map((v) => tagLabel(v))
              .join(' · ')}
          </div>
        ) : null}
      </div>
      {entry.hasHero ? (
        <img
          class="recipe__image"
          src={`/img/recipes/${slug}/hero.webp`}
          alt={recipe.title}
          width={entry.heroDims?.width}
          height={entry.heroDims?.height}
        />
      ) : null}

      <Settings
        recipe={recipe}
        appliedServings={appliedServings}
        onApply={setAppliedServings}
        unitSystem={unitSystem}
        onUnitSystemChange={setUnitSystem}
        selection={selection}
        onSelectionChange={(optKey, choiceKey) =>
          setSelection((s) => ({ ...s, [optKey]: choiceKey }))
        }
      />

      <CollapsibleSection title="Ingredients" icon="list" complete={allIngredientsChecked}>
        <ul class="checklist">
          {recipe.ingredients.map((ingredient, i) => (
            <IngredientItem
              key={ingredient.name}
              ingredient={ingredient}
              multiplier={multiplier}
              unitSystem={unitSystem}
              checked={checkedIngredients[i] ?? false}
              onToggle={() => toggleIngredient(i)}
            />
          ))}
        </ul>
      </CollapsibleSection>

      <CollapsibleSection
        title="Directions"
        icon="book"
        complete={allDirectionsChecked}
        actions={<AmountsToggle on={showAmounts} onToggle={() => setShowAmounts((s) => !s)} />}
      >
        <ol class="directions">
          {filteredDirections.map((direction, i) => (
            <Direction
              key={direction.title}
              direction={direction}
              checkedBullets={checkedDirections[i] ?? []}
              onToggleBullet={(b) => toggleBullet(i, b)}
              onToggleDirection={() => toggleDirection(i)}
              ingredientsByName={ingredientsByName}
              multiplier={multiplier}
              unitSystem={unitSystem}
              showAmounts={showAmounts}
              vessels={recipe.vessels}
            />
          ))}
        </ol>
      </CollapsibleSection>
    </>
  );
}
